/**
 * OrganizationPicker — Phase 1.7.3 component for the AffiliationsEditor
 * "+ Add affiliation" flow. Mirror of IndividualPicker's shape, inverted
 * for orgs: search existing organization OR inline-create a new one.
 *
 * The picker has two surfaces:
 *   1. Search input — debounced query against `api.searchProviders` (Q6
 *      pattern from OrgSearchAutocomplete); results filtered to
 *      type='organization'.
 *   2. Inline-create — "+ Add new organization" button reveals a
 *      sub-form with org-mode fields (name + category + service type +
 *      specialties + phone + secondary phone + fax + address + website +
 *      counties + medicaid + dementia + notes — same shape as Phase 1.7's
 *      SuggestResourceForm org-mode).
 *
 * Parent wires up the two callbacks:
 *   - `onPickExisting({ id, display_name })` — chosen existing org
 *   - `onInlineSubmit(payload)` — inline-create draft submitted; parent
 *     routes it through suggested_resources flow with contact_individual_id
 *     pointing at the current individual (per the opener's recommended
 *     "suggested_resources path for steward review consistency"). The new
 *     org enters the steward review queue; on approve, the
 *     contact_individual_id mechanism (mirror of Phase 1.7.1
 *     applySuggestedResource branch) auto-creates an affiliation linking
 *     the new org to the individual.
 *
 * Trade-off (Phase 1.7.3 compressed-cadence scope; Phase 1.8 may extend
 * per opener §"Cadence" forward-watch): operator's role/title/dates
 * intent for the new-org affiliation is NOT carried through the
 * suggested_resources flow in this iteration. The steward-created
 * affiliation defaults to role='Contact' (existing contact_individual_id
 * apply branch); steward can edit role/title/dates post-approve.
 *
 * UX-13 S5 — TypeScript; the action buttons compose <Button> (the
 * retired rounded-full pills + text-white + ring-sage-500 go with it);
 * the listbox option rows stay native buttons (an option row is not a
 * Button-class affordance), so the file stays consciously allowlisted.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { TextInput } from '@/components/primitives/TextInput';
import type { OrganizationSubmitPayload } from '@/lib/suggestedResources';
import type { SearchResult } from '@/lib/types';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { lookupsApi } from '@/lib/lookups';
import { CATEGORY_SLUG_TO_DISPLAY } from '@/lib/uiCategoryLabels';
import { formatCountyDisplay } from '@/lib/serviceAreas';
import { MultiSelectCheckbox } from '@/components/MultiSelectCheckbox';
import { MEDICAID_VALUES } from '@/lib/facetTaxonomy';
import { pageReadText } from '@/lib/errorCopy';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { SelectField } from '@/components/primitives/SelectField';
import { AddressCheckChoices, AddressCheckPrompt, AddressFields, useAddressForm, type AddressFormParts } from '@/components/clientRecord/AddressesCard';

const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;
const SEARCH_LIMIT = 20;
const LOOKUP_STALE_MS = 10 * 60 * 1000;

// The ONE facet source (facetTaxonomy header contract) — this list used
// to be a local duplicate and drifted risk; the 0129 label+value
// unification re-anchored it on the canonical module.
const MEDICAID_OPTIONS: ReadonlyArray<string> = MEDICAID_VALUES;

export interface OrganizationPickerProps {
  onPickExisting: (org: { id: string; display_name: string }) => void;
  onInlineSubmit: (payload: OrganizationSubmitPayload) => Promise<void> | void;
  onCancel: () => void;
}

export function OrganizationPicker({ onPickExisting, onInlineSubmit, onCancel }: OrganizationPickerProps): ReactNode {
  const { tenantSlug } = useTenant();
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // A failed search keeps its error and is worded at render, so the search
  // effect does not depend on the contact.
  const [searchError, setSearchError] = useState<unknown>(null);
  const contact = useSupportContact();
  const [isInlineOpen, setIsInlineOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isInlineOpen) inputRef.current?.focus();
  }, [isInlineOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const q = debouncedQ.trim();
    if (q.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSearchError(null);
    api
      .searchProviders(tenantSlug, { q, limit: SEARCH_LIMIT })
      .then((r) => {
        if (cancelled) return;
        setResults(r.rows.filter((row) => row.type === 'organization'));
      })
      .catch((e: unknown) => {
        if (!cancelled) setSearchError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, tenantSlug]);

  if (isInlineOpen) {
    return (
      <OrgInlineForm
        tenantSlug={tenantSlug}
        onSubmit={async (payload) => {
          await onInlineSubmit(payload);
          setIsInlineOpen(false);
        }}
        onCancel={() => setIsInlineOpen(false)}
      />
    );
  }

  const showEmptyHint =
    !loading && debouncedQ.trim().length >= MIN_CHARS && results.length === 0;

  return (
    <div>
      <label htmlFor="organization-picker-search" className="block text-sm text-ink mb-1">
        Search organizations
      </label>
      <SearchField
        ref={inputRef}
        id="organization-picker-search"
        value={query}
        onChange={setQuery}
        placeholder="Search by name"
        testId="organization-picker-search"
      />

      {loading && (
        <p className="mt-1 text-xs text-ink-soft" aria-live="polite">
          Searching…
        </p>
      )}

      {results.length > 0 && (
        <ul
          role="listbox"
          aria-label="Organization search results"
          className="mt-1 max-h-64 overflow-y-auto bg-white border border-sage-800/[8%] rounded-md text-sm"
        >
          {results.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  onPickExisting({ id: row.id, display_name: row.display_name })
                }
                className="block w-full text-left px-3 py-2 hover:bg-sage-50 min-h-[44px]"
              >
                <span className="font-semibold text-ink">{row.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showEmptyHint && (
        <p className="mt-1 text-xs text-ink-soft">
          Add a new organization below.
        </p>
      )}

      {searchError !== null && (
        <div role="alert" className="mt-1 text-xs text-warm-700" data-testid="org-picker-search-error">
          ⚠ {pageReadText(searchError, contact, 'the organizations')}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <Button
          variant="secondary"
          size="sm"
          icon={<span aria-hidden="true">+</span>}
          onClick={() => setIsInlineOpen(true)}
        >
          Add new organization
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Inline-create sub-form. Mirror of SuggestResourceForm org-mode field set
 * (name + category + service types + specialties + phone + phone_2 + fax +
 * address + website + counties + medicaid + dementia + notes). Routes the
 * payload through suggested_resources at parent level — the picker itself
 * is wire-agnostic; it just collects the org-mode payload and hands it off.
 *
 */
interface OrgInlineFormProps {
  tenantSlug: string;
  onSubmit: (payload: OrganizationSubmitPayload) => Promise<void> | void;
  onCancel: () => void;
}

function OrgInlineForm({ tenantSlug, onSubmit, onCancel }: OrgInlineFormProps): ReactNode {
  const [name, setName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [subcatSlugs, setSubcatSlugs] = useState<string[]>([]);
  const [specSlugs, setSpecSlugs] = useState<string[]>([]);
  const [countySlugs, setCountySlugs] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [fax, setFax] = useState('');
  // The address: the shared address form, a Directory address (a town
  // and a state at least); a street is checked before the submit.
  const address = useAddressForm({
    existing: null,
    rule: 'directory',
    idPrefix: 'org-picker',
    done: false,
    saving: false,
    onSave: (parts) => void send(parts),
  });
  const [website, setWebsite] = useState('');
  const [medicaid, setMedicaid] = useState('');
  const [dementiaUnit, setDementiaUnit] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const contact = useSupportContact();

  const subcatsQuery = useQuery({
    queryKey: ['lookups', 'subcategories', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSubcategories(tenantSlug, { limit: 500 }, signal),
    staleTime: LOOKUP_STALE_MS,
  });
  const specsQuery = useQuery({
    queryKey: ['lookups', 'specialties', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    staleTime: LOOKUP_STALE_MS,
  });
  const countiesQuery = useQuery({
    queryKey: ['lookups', 'service-areas', 'county', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(
        tenantSlug,
        { area_type: 'county', limit: 500 },
        signal,
      ),
    staleTime: LOOKUP_STALE_MS,
  });

  const categoryOptions = useMemo(
    () => Object.entries(CATEGORY_SLUG_TO_DISPLAY),
    [],
  );

  async function handleSubmit() {
    if (name.trim().length === 0) {
      setError("Enter the organization's name.");
      return;
    }
    if (categorySlug.length === 0) {
      setError('Pick a service category.');
      return;
    }
    setError(null);
    if (!address.isEmpty()) {
      address.pressSave();
      return;
    }
    await send(null);
  }

  async function send(addressParts: AddressFormParts | null) {
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        category_slug: categorySlug,
        kind: 'other',
        subcategories: subcatSlugs,
        specialties: specSlugs,
        counties: countySlugs,
        phone: nullableTrim(phone),
        phone_2: nullableTrim(phone2),
        fax: nullableTrim(fax),
        address: addressParts,
        website: nullableTrim(website),
        medicaid: medicaid === '' ? null : medicaid,
        dementia_unit: dementiaUnit,
        notes: nullableTrim(notes),
      });
    } catch (err) {
      setError(suggestionRefusalText(err, contact));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-2 p-3 border border-sage-800/[8%] bg-white rounded-md">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-2">
        New organization
      </p>
      <p className="text-xs text-ink-soft mb-3">
        An admin reviews it first. Once they approve, you'll see the affiliation on
        this contact.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-2 p-2 rounded-md bg-warm-50 border border-warm-300 text-xs text-ink"
          data-testid="org-picker-submit-error"
        >
          {error}
        </div>
      )}

      <div className="space-y-2">
        <SubField label="Organization name" required>
          <TextInput
            type="text"
            nameCase="place"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls()}
            aria-required="true"
          />
        </SubField>

        <SubField label="Service category" required>
          <SelectField size="field"
            value={categorySlug}
            onChange={(e) => setCategorySlug(e.target.value)}
            className="w-full"
            aria-required="true"
          >
            <option value="">Select a category…</option>
            {categoryOptions.map(([slug, display]) => (
              <option key={slug} value={slug}>
                {display}
              </option>
            ))}
          </SelectField>
        </SubField>

        <SubField label="Service types">
          <MultiSelectCheckbox
            options={subcatsQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => r.display_name}
            selectedValues={subcatSlugs}
            onChange={setSubcatSlugs}
            loading={subcatsQuery.isLoading}
            ariaLabel="Service types"
          />
        </SubField>

        <SubField label="Specialties">
          <MultiSelectCheckbox
            options={specsQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => r.display_name}
            selectedValues={specSlugs}
            onChange={setSpecSlugs}
            loading={specsQuery.isLoading}
            ariaLabel="Specialties"
          />
        </SubField>

        <SubField label="Counties served">
          <MultiSelectCheckbox
            options={countiesQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => formatCountyDisplay(r.display_name)}
            selectedValues={countySlugs}
            onChange={setCountySlugs}
            loading={countiesQuery.isLoading}
            ariaLabel="Counties served"
            filterPlaceholder="Filter counties"
          />
        </SubField>

        <SubField label="Phone">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputCls()}
          />
        </SubField>

        <SubField label="Secondary phone">
          <input
            type="tel"
            value={phone2}
            onChange={(e) => setPhone2(e.target.value)}
            className={inputCls()}
          />
        </SubField>

        <SubField label="Fax">
          <input
            type="tel"
            value={fax}
            onChange={(e) => setFax(e.target.value)}
            className={inputCls()}
          />
        </SubField>

        <AddressFields idPrefix="org-picker" {...address.fields} />
        <AddressCheckPrompt phase={address.check.phase} />
        {address.prompting && (
          <div className="flex flex-wrap justify-end gap-2">
            <AddressCheckChoices
              phase={address.check.phase}
              busy={submitting}
              onKeep={() => address.choose('checked', false)}
              onUse={() => address.choose('checked', true)}
              onSaveAsTyped={() => address.choose('saved_as_typed', false)}
            />
          </div>
        )}

        <SubField label="Website">
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://"
            className={inputCls()}
          />
        </SubField>

        <SubField label="Medicaid">
          <SelectField size="field"
            value={medicaid}
            onChange={(e) => setMedicaid(e.target.value)}
            className="w-full"
          >
            <option value="">Unspecified</option>
            {MEDICAID_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </SelectField>
        </SubField>

        <div className="flex items-center gap-2">
          <input
            id="inline-org-dementia"
            type="checkbox"
            checked={dementiaUnit}
            onChange={(e) => setDementiaUnit(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="inline-org-dementia" className="text-sm text-ink">
            Dementia unit
          </label>
        </div>

        <SubField label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls()}
          />
        </SubField>
      </div>

      <div className="flex justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} loading={submitting}>
          {submitting ? 'Submitting…' : 'Submit for review'}
        </Button>
      </div>
    </div>
  );
}

function SubField({ label, required, children }: { label: string; required?: boolean; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink mb-1">
        {label}
        {required && <span className="text-warm-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function inputCls(): string {
  // Geometry ruling §12: inputs take rounded-base; the standard sage-400 ring.
  return 'w-full min-h-9 px-3 py-1.5 border border-sage-300 rounded-base text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
}

function nullableTrim(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}
